import { CSSProperties, useMemo } from 'react';
import { bbkApiSlice } from './bbkApi.slice';
import { RestCard } from '@bbkAdminUtils/api-client/rest-experiences-api';
import { EmotionTheme } from '@bbkFunnel/pages/theme';
import {
  BillingAttachedItem,
  BillingItem,
  Price,
  PricingTable,
  Subscription,
} from '@bbkPricingPage/types/pricing-table-types';

// args for loading PC data
export type GetChargebeeItemsArgs = {
  billingSystemIds?: string[];
  itemTypes?: string[];
  appKey: string;
};

export type GetChargebeeItemPriceArgs = {
  billingSystemIds?: string[];
  billingSystemItemIds?: string[];
  appKey: string;
};

export type GetChargebeeAttachedItemsArgs = {
  billingSystemItemIds?: string[];
  appKey: string;
};

// Pricing page args
export type GetPricingPagesArgs = {
  appKey: string;
  active_only?: boolean;
  brand_id?: string;
};

export type RestPricingPage = {
  id: string;
  name: string;
  application_id: string;
  active: boolean;
  modified_at: string;
  created_at: string;
  updated_at: string;
};

export type GetPricingPageArgs = {
  appKey: string;
  pricingPageId: string;
};

export type GetPricingPageRenderArgs = {
  appKey: string;
  pricingPageId: string;
  publish_state?: 'draft' | 'live';
};

export type CreatePricingPageArgs = {
  appKey: string;
  body: {
    name: string;
    brand_id?: string;
  };
};

export type UpdatePricingPageArgs = {
  appKey: string;
  pricingPageId: string;
  body: {
    active?: boolean;
    name?: string;
  };
};

export type PublishPricingPageArgs = {
  appKey: string;
  pricingPageId: string;
};

type RestPricingTable = {
  id: string;
  name: string;
  version: string;
  template: string;
  pricing_element: Pick<
    PricingTable,
    | 'grouped_by'
    | 'frequencies'
    | 'currencies'
    | 'items'
    | 'addons'
    | 'customization'
  >;
};

export type RestPricingPageLayout = {
  cards: RestCard[];
  style_overlay: Partial<
    Partial<EmotionTheme> & {
      font: Partial<{
        fontFamily: CSSProperties['fontFamily'];
        href: string;
        baseFontSize: string;
        baseFontWeight: string;
      }>;
    }
  >;
  pricing_table: RestPricingTable;
};

export type RestPricingPageJson = {
  experience: {
    version: string;
    layout: RestPricingPageLayout;
  };
  draft_published?: boolean;
  draft_publish_date?: string;
};

export type SaveDraftPricingPageArgs = {
  appKey: string;
  pricingPageId: string;
  body: {
    cards?: RestCard[];
    pricing_table?: RestPricingTable;
  };
};

export type GetPreviewSubscriptionsArgs = {
  appKey: string;
};

export type GetBillingSubscriptionsArgs = {
  appKey: string;
  integration: string;
  limit?: number;
  offset?: string;
};

export type BillingSubscriptionsResponse = {
  subscriptions: Subscription[];
  next_offset?: string;
};
export type GetPublicUrlArgs = {
  appKey: string;
  pricingPageId: string;
};

export type GetPublicUrlResponse = {
  url: string;
};

export const pricingPagesSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getChargebeeItems: build.query<BillingItem[], GetChargebeeItemsArgs>({
      query: ({ appKey, billingSystemIds, itemTypes }) => ({
        url: `/api/v1/apps/${appKey}/pricing/items`,
        method: 'GET',
        params: {
          billing_system_ids: billingSystemIds?.join(','),
          item_types: itemTypes?.join(','),
        },
      }),
    }),
    getChargebeeItemPrices: build.query<Price[], GetChargebeeItemPriceArgs>({
      query: ({ appKey, billingSystemIds, billingSystemItemIds }) => ({
        url: `/api/v1/apps/${appKey}/pricing/prices`,
        method: 'GET',
        params: {
          billing_system_ids: billingSystemIds?.join(','),
          billing_system_item_ids: billingSystemItemIds?.join(','),
        },
      }),
    }),
    getChargebeeAttachedItems: build.query<
      BillingAttachedItem[],
      GetChargebeeAttachedItemsArgs
    >({
      query: ({ appKey, billingSystemItemIds }) => ({
        url: `/api/v1/apps/${appKey}/pricing/attached-items`,
        method: 'GET',
        params: {
          billing_system_item_ids: billingSystemItemIds?.join(','),
        },
      }),
    }),
    getZeroDecimalCurrencies: build.query<string[], void>({
      query: () => ({
        url: `/api/v2/zero_decimal_currencies`,
      }),
    }),
    getPricingPagesList: build.query<RestPricingPage[], GetPricingPagesArgs>({
      query: ({ appKey, brand_id }) => ({
        url: `/api/v2/apps/${appKey}/pricing-pages`,
        params: {
          ...(brand_id && { brand_id }),
        },
      }),
      providesTags: (result) => [
        { type: 'PricingPage', id: 'LIST' },
        ...(result?.map(({ id }) => ({ type: 'PricingPage' as const, id })) ??
          []),
      ],
    }),
    createPricingPage: build.mutation<RestPricingPage, CreatePricingPageArgs>({
      query: ({ appKey, body }) => ({
        url: `/api/v2/apps/${appKey}/pricing-pages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'PricingPage', id: 'LIST' }],
    }),
    getPricingPage: build.query<RestPricingPage, GetPricingPageArgs>({
      query: ({ appKey, pricingPageId }) => ({
        url: `/api/v2/apps/${appKey}/pricing-pages/${pricingPageId}`,
      }),
      providesTags: (result, error, { pricingPageId }) => [
        { type: 'PricingPage', id: pricingPageId },
      ],
    }),
    updatePricingPage: build.mutation<RestPricingPage, UpdatePricingPageArgs>({
      query: ({ appKey, pricingPageId, body }) => ({
        url: `/api/v2/apps/${appKey}/pricing-pages/${pricingPageId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { pricingPageId }) => [
        { type: 'PricingPage', id: 'LIST' },
        { type: 'PricingPage', id: pricingPageId },
      ],
    }),
    getPricingPageRender: build.query<
      RestPricingPageJson,
      GetPricingPageRenderArgs
    >({
      query: ({ appKey, pricingPageId, publish_state }) => ({
        url: `/api/v1/apps/${appKey}/pricing_pages/${pricingPageId}/table`,
        params: publish_state ? { publish_state } : {},
      }),
      providesTags: (result, error, args) => [
        { type: 'PricingTable', id: args.pricingPageId },
      ],
    }),
    publishPricingPage: build.mutation<void, PublishPricingPageArgs>({
      query: ({ appKey, pricingPageId }) => ({
        url: `/api/v1/apps/${appKey}/pricing_pages/${pricingPageId}/publish`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, args) => [
        { type: 'PricingTable', id: args.pricingPageId },
      ],
    }),
    saveDraftPricingPage: build.mutation<void, SaveDraftPricingPageArgs>({
      query: ({ appKey, pricingPageId, body }) => ({
        url: `/api/v2/apps/${appKey}/pricing-pages/${pricingPageId}/table`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, args) => [
        { type: 'PricingTable', id: args.pricingPageId },
      ],
    }),
    getPreviewSubscriptions: build.query<
      Subscription[],
      GetPreviewSubscriptionsArgs
    >({
      query: ({ appKey }) => ({
        url: `/api/v1/apps/${appKey}/pricing_pages/preview-subscriptions`,
      }),
    }),
    getBillingSubscriptions: build.query<
      BillingSubscriptionsResponse,
      GetBillingSubscriptionsArgs
    >({
      query: ({ appKey, integration, limit, offset }) => ({
        url: `/api/v1/apps/${appKey}/billing/${integration}/subscriptions`,
        params: {
          ...(limit && { limit }),
          ...(offset && { offset }),
        },
      }),
    }),
    getPublicUrl: build.query<GetPublicUrlResponse, GetPublicUrlArgs>({
      query: ({ appKey, pricingPageId }) => ({
        url: `/api/v1/apps/${appKey}/pricing_pages/${pricingPageId}/public_url`,
      }),
    }),
  }),
});

export const useGetPricingPagesList = (args: GetPricingPagesArgs) => {
  const { data } =
    pricingPagesSlice.endpoints.getPricingPagesList.useQuery(args);
  return useMemo(() => data ?? [], [data]);
};

export const useGetPricingPageRender = (
  args: GetPricingPageRenderArgs,
  options?: { skip?: boolean }
) => {
  const { data, isLoading, error } =
    pricingPagesSlice.endpoints.getPricingPageRender.useQuery(args, {
      skip: options?.skip,
    });
  return useMemo(
    () => ({
      data: data ?? undefined,
      isLoading,
      error,
    }),
    [data, isLoading, error]
  );
};

export const useGetBillingSubscriptions = (
  args: GetBillingSubscriptionsArgs,
  options?: { skip?: boolean }
) => {
  const { data, error, isLoading, isFetching } =
    pricingPagesSlice.endpoints.getBillingSubscriptions.useQuery(args, {
      skip: options?.skip,
    });

  return useMemo(
    () => ({
      subscriptions: data?.subscriptions ?? [],
      nextOffset: data?.next_offset,
      hasMore: !!data?.next_offset,
      isLoading,
      isFetching,
      error,
    }),
    [data, error, isLoading, isFetching]
  );
};
